/**
 * Analytics Pulse Report Templates
 * Based on best practices from Google Analytics, Mixpanel, Amplitude
 */

import { PulseReportTemplate } from "./types";

export const MONTHLY_ANALYTICS: PulseReportTemplate = {
  id: "monthly-analytics",
  name: "Monthly Analytics Report",
  reportType: "analytics",
  description: "Comprehensive monthly analytics with traffic and engagement",
  purpose:
    "Provide actionable monthly insights. Demonstrate platform value through data.",
  icon: "LineChartOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Your {{current_month}} Analytics Report",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Account owners and managers",
  expectedOutcomes: [
    "Increased platform engagement",
    "Data-driven decision making",
    "Clear ROI demonstration",
    "Higher retention through value proof",
  ],
  bestPractices: [
    "Send first week of the month",
    "Compare to previous month",
    "Highlight top 3 insights",
    "Include actionable recommendations",
  ],
  keyMetrics: [
    "Total visitors",
    "Engagement rate",
    "Conversion rate",
    "Top traffic sources",
    "Geographic breakdown",
  ],
  tags: ["analytics", "monthly", "traffic", "engagement", "insights"],
  bestFor: ["Marketing teams", "Account managers", "Business owners"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Rich analytics dashboard in email format",
};

export const WEEKLY_DIGEST: PulseReportTemplate = {
  id: "weekly-digest",
  name: "Weekly Performance Digest",
  reportType: "analytics",
  description: "Weekly snapshot of key performance metrics",
  purpose:
    "Keep stakeholders informed with a quick weekly overview. Perfect for busy executives.",
  icon: "DashboardOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "weekly-digest",
  suggestedSubject: "Your Week in Numbers - {{week_dates}}",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "08:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Executives and decision makers",
  expectedOutcomes: [
    "60%+ open rate",
    "Quick weekly check-in habit",
    "Early issue detection",
    "Stakeholder alignment",
  ],
  bestPractices: [
    "Keep it scannable - key numbers only",
    "Send Monday morning",
    "Include week-over-week trends",
    "Highlight anomalies immediately",
  ],
  keyMetrics: [
    "Total conversations",
    "Resolution rate",
    "Satisfaction score",
    "Week-over-week change",
    "Top highlights",
  ],
  tags: ["digest", "weekly", "executive", "summary", "snapshot"],
  bestFor: ["Executive briefings", "Team updates", "Quick reviews"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Scannable weekly KPI snapshot",
};

export const TRAFFIC_INSIGHTS: PulseReportTemplate = {
  id: "traffic-insights",
  name: "Traffic Insights Report",
  reportType: "analytics",
  description: "Deep dive into traffic sources and visitor behavior",
  purpose:
    "Help marketing teams understand traffic patterns and optimize acquisition.",
  icon: "GlobalOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Traffic Insights: Where your visitors come from",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 15,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: true,
    team: false,
  },
  targetAudience: "Marketing teams and growth managers",
  expectedOutcomes: [
    "Better channel attribution",
    "Optimized marketing spend",
    "Improved conversion paths",
    "Data-driven campaign decisions",
  ],
  bestPractices: [
    "Break down by channel (organic, paid, social, referral)",
    "Show conversion rate per channel",
    "Include geographic insights",
    "Highlight top performing sources",
  ],
  keyMetrics: [
    "Traffic by channel",
    "Conversion by source",
    "Top referrers",
    "Geographic distribution",
    "Device breakdown",
  ],
  tags: ["traffic", "marketing", "acquisition", "channels", "insights"],
  bestFor: ["Marketing teams", "Growth teams", "Campaign optimization"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Marketing-focused traffic analysis",
};

export const ENGAGEMENT_REPORT: PulseReportTemplate = {
  id: "engagement-report",
  name: "User Engagement Report",
  reportType: "analytics",
  description: "Track how users engage with your chatbots",
  purpose:
    "Understand user engagement patterns to optimize chatbot performance.",
  icon: "InteractionOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "Engagement Report: How users interact with your bots",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 5,
    time: "14:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Product managers and UX teams",
  expectedOutcomes: [
    "Improved chatbot user experience",
    "Higher completion rates",
    "Better understanding of user needs",
    "Data-driven UX improvements",
  ],
  bestPractices: [
    "Track conversation depth",
    "Monitor drop-off points",
    "Analyze most common queries",
    "Segment by user type",
  ],
  keyMetrics: [
    "Avg conversation length",
    "Completion rate",
    "Most popular intents",
    "Drop-off points",
    "Return user rate",
  ],
  tags: ["engagement", "ux", "behavior", "interactions", "analytics"],
  bestFor: ["UX optimization", "Product improvement", "Feature planning"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Deep engagement analytics and patterns",
};

export const ANALYTICS_TEMPLATES = [
  MONTHLY_ANALYTICS,
  WEEKLY_DIGEST,
  TRAFFIC_INSIGHTS,
  ENGAGEMENT_REPORT,
];
